import { useEffect } from 'react';
import { saveBarCache, loadBarCacheEntry } from './useBarCache';
import { useTerminalStore } from '../store/useTerminalStore';
import { gatewayApi } from '../lib/api';
import { fetchBarsWithFallback } from '../lib/bars';
import type { ComboStatus, ComboGenome } from '../types/gateway';
import {
  generateMockCandles,
  generateMockGenomes,
  generateMockAlphaEval,
  generateMockCombos,
  generateMockRdLoop,
  generateMockRisk,
  generateMockPortfolio,
  generateMockVarCone,
} from '../fixtures/mockData';

async function fetchOrNull<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch {
    return null;
  }
}

export function useBootstrap(enabled: boolean = true): void {
  useEffect(() => {
    if (!enabled) return;
    if (useTerminalStore.getState().dataSource !== 'pending') return;

    let cancelled = false;

    // DDD #180 S1.2: record a panel's per-fetch outcome so usePanelState can
    // distinguish live-empty (200, no rows yet) from degraded (fetch failed).
    // Additive — does not alter the returned value or the happy-path data flow.
    async function tracked<T>(key: string, fn: () => Promise<T>): Promise<T | null> {
      const { setPanelStatus } = useTerminalStore.getState();
      try {
        const r = await fn();
        const empty =
          r == null ||
          (Array.isArray(r) && r.length === 0) ||
          (typeof r === 'object' && !Array.isArray(r) && Object.keys(r as object).length === 0);
        setPanelStatus(key, empty ? 'live-empty' : 'ready');
        return r;
      } catch {
        setPanelStatus(key, 'degraded');
        return null;
      }
    }

    async function trackedForSymbol<T>(key: string, symbol: string, fn: () => Promise<T>): Promise<T | null> {
      try {
        const r = await fn();
        if (useTerminalStore.getState().selectedSymbol === symbol) {
          const empty =
            r == null ||
            (Array.isArray(r) && r.length === 0) ||
            (typeof r === 'object' && !Array.isArray(r) && Object.keys(r as object).length === 0);
          useTerminalStore.getState().setPanelStatus(key, empty ? 'live-empty' : 'ready');
        }
        return r;
      } catch {
        if (useTerminalStore.getState().selectedSymbol === symbol) {
          useTerminalStore.getState().setPanelStatus(key, 'degraded');
        }
        return null;
      }
    }

    async function seedFromGateway() {
      try {
        const bootSymbol = useTerminalStore.getState().selectedSymbol;
        const cachedEntry = loadBarCacheEntry(bootSymbol, 'dollar');
        if (cachedEntry && cachedEntry.bars.length > 0) {
          useTerminalStore.setState({ bars: cachedEntry.bars });
          useTerminalStore.getState().setBarsSource('dollar', cachedEntry.isFallback);
        }
        const health = await gatewayApi.health();
        // Additive upstream health (absent on older gateways → null = unknown).
        useTerminalStore.getState().setUpstreamHealth(health.upstream_health ?? null);
        if (cancelled) return;

        // Last 30 days in ms
        const now = Date.now();
        const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;

        const [barsResult, arsenal, metrics, positions, account, genomeList, evalList, signalList, comboStatusList, externalList, regimeHistoryList, equityHistoryList, varConeData] = await Promise.all([
          fetchBarsWithFallback(bootSymbol, undefined, 500),
          gatewayApi.arsenal(),
          gatewayApi.metrics(),
          gatewayApi.positions(),
          fetchOrNull(() => gatewayApi.account()),
          tracked('combo', () => gatewayApi.genome()),
          fetchOrNull(() => gatewayApi.alphaEval()),
          tracked('signals', () => gatewayApi.signals()),
          fetchOrNull(() => gatewayApi.comboStatus()),
          fetchOrNull(() => gatewayApi.external()),
          fetchOrNull(() => gatewayApi.regimeHistory(now - thirtyDaysMs, now)),
          tracked('equity', () => gatewayApi.portfolioEquity(now - thirtyDaysMs, now)),
          trackedForSymbol('varcone', bootSymbol, () => gatewayApi.varcone(bootSymbol)),
        ]);

        if (cancelled) return;

        // DDD #180 S1.2: the signals fetch feeds three panels (SignalFeed,
        // TradeHistory, ActionBoardPanel). Fan its outcome to all three keys so
        // each resolves the same tri-state.
        const sigStatus = useTerminalStore.getState().panelStatus['signals'] ?? 'live-empty';
        useTerminalStore.getState().setPanelStatus('trades', sigStatus);
        useTerminalStore.getState().setPanelStatus('actionboard', sigStatus);

        const combos: Record<string, ComboStatus> = {};
        if (comboStatusList && comboStatusList.length > 0) {
          for (const cs of comboStatusList) combos[cs.combo] = cs;
        } else {
          for (const c of arsenal) {
            combos[c.name] = {
              combo: c.name,
              win_rate: c.win_rate,
              pnl: 0,
              weight: arsenal.length > 0 ? 1 / arsenal.length : 0,
              cusum_score: c.sharpe,
              enabled: c.status === 'active',
            };
          }
        }

        const genomes: Record<string, ComboGenome> = {};
        if (genomeList && genomeList.length > 0) {
          for (const g of genomeList) genomes[g.name] = g;
        }

        const jobsList = await fetchOrNull(() => gatewayApi.jobs());
        const instrumentList = await fetchOrNull(() => gatewayApi.instruments());
        const ordersList = await fetchOrNull(() => gatewayApi.listOrders());

        const mappedBars = barsResult.bars.map((b) => ({
          time: new Date(b.bar_close_time).getTime() / 1000,
          open: b.open,
          high: b.high,
          low: b.low,
          close: b.close,
          volume: b.volume,
        }));
        const dedupBars = new Map<number, typeof mappedBars[0]>();
        for (const bar of mappedBars) dedupBars.set(bar.time, bar);
        const sortedBars = [...dedupBars.values()].sort((a, b) => a.time - b.time);
        // Cache under the requested default timeframe ('dollar') — matches the
        // bootstrap cache read above — so a 1m-fallback result is found on next load
        // instead of being written under '1m' and missed.
        saveBarCache(bootSymbol, 'dollar', sortedBars, barsResult.isFallback);
        // Record fallback state so the chart can show the 1m-proxy badge.
        useTerminalStore.getState().setBarsSource(barsResult.timeframeUsed, barsResult.isFallback);

        const riskMetrics = await tracked('risk', () => gatewayApi.riskMetrics());
        const portfolioData = await tracked('portfolio', () => gatewayApi.portfolioData());
        const regimeState = await tracked('regime', () => gatewayApi.regime());

        if (cancelled) return;

        useTerminalStore.setState({
          bars: sortedBars,
          combos,
          metrics,
          positions: positions.map((p) => {
            const sign = p.side === 'Sell' ? -1 : 1;
            const qty = sign * p.quantity;
            const pnl = p.unrealized_pnl !== 0
              ? p.unrealized_pnl
              : (p.current_price - p.entry_price) * sign * p.quantity;
            return {
              symbol: p.symbol,
              qty,
              entry_price: p.entry_price,
              current_price: p.current_price,
              unrealized_pnl: pnl,
              view: 'HEDGING' as const,
            };
          }),
          genomes,
          alphaEvals: evalList && evalList.length > 0
            ? Object.fromEntries(evalList.map((e) => [e.combo, e]))
            : {},
          signals: signalList ?? [],
          rdLoopState: null,
          jobs: jobsList ?? [],
          orders: ordersList ?? [],
          accountBalance: account,
          externalFeeds: externalList ?? [],
          riskMetrics,
          portfolioData,
          varCone: useTerminalStore.getState().selectedSymbol === bootSymbol
            ? varConeData
            : useTerminalStore.getState().varCone,
          regimeState,
          regimeHistory: regimeHistoryList ?? [],
          equityHistory: equityHistoryList ?? [],
          instruments: instrumentList ? Object.fromEntries(instrumentList.map((i) => [i.symbol, i])) : {},
          dataSource: 'live',
        });
      } catch (err) {
        if (cancelled) return;
        // DDD #180 S1: telemetry — never fail silently into mock data.
        console.error('[bootstrap] gateway bootstrap failed — degraded fallback', err);
        // DDD #180 S1.2: total failure → mark tracked panels degraded so they
        // show a 'degraded' state (not a stuck 'loading') when their mock value
        // is absent. Panels whose mock value is present still render that data.
        const { setPanelStatus } = useTerminalStore.getState();
        for (const key of ['risk', 'portfolio', 'equity', 'combo', 'signals', 'trades', 'actionboard', 'regime']) setPanelStatus(key, 'degraded');
        seedFromMock(err);
      }
    }

    function seedFromMock(err?: unknown) {
      const mockCombos: Record<string, ComboStatus> = {};
      for (const c of generateMockCombos()) mockCombos[c.combo] = c;

      const mockGenomes: Record<string, ComboGenome> = {};
      for (const g of generateMockGenomes()) mockGenomes[g.name] = g;

      const mockAlphaEval = generateMockAlphaEval();

      useTerminalStore.setState({
        bars: generateMockCandles(200),
        combos: mockCombos,
        genomes: mockGenomes,
        alphaEvals: { [mockAlphaEval.combo]: mockAlphaEval },
        signals: [],
        rdLoopState: generateMockRdLoop(),
        jobs: [],
        accountBalance: null,
        externalFeeds: [],
        riskMetrics: generateMockRisk(),
        portfolioData: generateMockPortfolio(),
        varCone: generateMockVarCone(),
        regimeState: null,
        // Reset the 1m-fallback badge: a stale isFallback (set from cache before the
        // network bootstrap ran) must not persist onto the mock/degraded bars below.
        barsIsFallback: false,
        barsTimeframeUsed: 'dollar',
        // DDD #180 S1: seedFromMock is only ever reached from the bootstrap
        // catch path, i.e. a genuine gateway failure. Surface it as a VISIBLE
        // 'degraded' state (DegradedDataBanner) instead of silent fake data.
        dataSource: 'degraded',
        bootstrapError: err != null ? String(err) : 'unknown bootstrap failure',
      });
    }

    seedFromGateway();

    return () => {
      cancelled = true;
    };
  }, [enabled]);
}
